import { parseServerDate } from "src/utils/dateTime";
import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDateTime } from "src/utils/dateTime";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Spacer from "@ui/spacer/Spacer";
import { ProgressBar } from "@ui/spinner/ProgressBar";
import { Typo } from "@ui/text/Typo";
import { useRef, useState, useEffect } from "react";
import { GD } from "src/GD";
import { IPagination, Pagination } from "../Pagination";
import { StatusBadge } from "../StatusBadge";
import { Table } from "@ui/table/Table";
import { DateRangePicker, toUTCISODate } from "src/view/conponents/DateRangePicker";
import { HoverTooltip, FailReasonHint } from "../HoverTooltip";
import { RecoDetailsPopup } from "../popup/RecoDetailsPopup";

interface IRecoItem {
	id: number;
	decta_payment_id: number;
	yyyymm: number;
	created_at: string;
	status: string;
	fail_reason_id: number | null;
	merchant_id: string;
	fail_reason?: string;
}

const perPage = 20;

const defaultFromDate = () => {
	const d = new Date();
	d.setUTCDate(d.getUTCDate() - 30);
	d.setUTCHours(0, 0, 0, 0);
	return d;
};

const formatDateParam = (date: Date): string => {
	const y = date.getUTCFullYear();
	const m = String(date.getUTCMonth() + 1).padStart(2, "0");
	const d = String(date.getUTCDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
};

export const Reco = () => {
	useLocale();
	const [currentPage, setCurrentPage] = useState<number>(0);
	const [selectedId, setSelectedId] = useState<number | null>(null);
	const scrollRef = useRef<HTMLDivElement>(null);
	const [headerHeight] = GD.B_HEADER_HEIGHT.use(undefined, undefined, []);

	const [fromDate, setFromDate] = useState<Date>(defaultFromDate());
	const [toDate, setToDate] = useState<Date>(new Date());

	const getRequestData = (page: number) => ({
		method: "processing/reco",
		data: { page, limit: perPage, from_date: formatDateParam(fromDate), to_date: formatDateParam(toDate) },
	});

	const [response, reload, _reset, busy] = GD.REQ_API.useRequest(null, getRequestData(currentPage));

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		reload(getRequestData(page));
	};

	const handleRefresh = () => {
		reload(getRequestData(currentPage));
	};

	const paginationData: IPagination | undefined = response?.data?.pagination;
	const items = response?.data?.items as IRecoItem[] | undefined;

	const headerCount = 6;
	const stickyHeadSx = {
		position: "relative" as const,
		zIndex: 2,
		backgroundColor: "#d1dbe6",
		borderTop: "20px solid #f8fafc",
	};

	useEffect(() => {
		const wrapper = scrollRef.current;
		if (!wrapper || !items?.length) return;

		const grid = wrapper.firstElementChild as HTMLElement;
		if (!grid) return;

		const headerCells = Array.from(grid.children).slice(0, headerCount) as HTMLElement[];
		if (headerCells.length === 0) return;

		const stickyTop = headerHeight ?? 0;

		const handleScroll = () => {
			const wrapperRect = wrapper.getBoundingClientRect();
			const offset = stickyTop - wrapperRect.top;
			const maxOffset = grid.offsetHeight - headerCells[0].offsetHeight;
			const clamped = Math.round(Math.max(0, Math.min(offset, maxOffset)));

			for (const cell of headerCells) {
				cell.style.transform = clamped > 0 ? `translateY(${clamped}px)` : "";
			}
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		handleScroll();

		return () => window.removeEventListener("scroll", handleScroll);
	}, [items, headerHeight]);

	return (
		<VBox sx={{ flex: 1, minWidth: 0 }}>
			<Box
				sx={{
					width: "100%",
					display: "flex",
					flexWrap: "wrap",
					gap: "1px",
					backgroundColor: "rgba(0,0,0,0.06)",
					borderRadius: "4px",
					boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
				}}>
				<Box
					sx={{
						padding: "14px 20px",
						alignItems: "center",
						justifyContent: "flex-start",
						display: "flex",
						backgroundColor: "#fff",
						flex: "1 1 auto",
					}}>
					<HBox gap="8px" sx={{ flexWrap: "wrap", alignItems: "end" }}>
						<DateRangePicker timeZone="UTC"
							style={{ width: "290px", maxWidth: "100%" }}
							label={msg("dateRange")}
							from={toUTCISODate(fromDate)}
							to={toUTCISODate(toDate)}
							onChange={(from, to) => {
								setFromDate(parseServerDate(from) ?? defaultFromDate());
								setToDate(parseServerDate(to) ?? new Date());
							}}
						/>
						<Button onClick={() => handleRefresh()} title={msg("search")} variant="primarySmall" />
					</HBox>
				</Box>
			</Box>

			<Spacer size="10px" />
			<ProgressBar busy={busy} />

			{response?.error && (
				<Box sx={{ padding: "20px" }}>
					<Typo variant="medium" sx={{ color: "#DC2626" }}>
						{response.error.message ?? msg("errorLoadingReco")}
					</Typo>
				</Box>
			)}

			{!busy && (
				<div ref={scrollRef} style={{ overflowX: "auto" }}>
					<Table<IRecoItem>
						data={items ?? []}
						onRowClick={row => setSelectedId(row.id)}
						order={["created_at", "id", "merchant_id", "status"]}
						headers={[
							{ value: "Date", gridColumn: "auto", sx: stickyHeadSx },
							{ value: "ID", gridColumn: "auto", sx: stickyHeadSx },
							{ value: "Merchant", gridColumn: "1fr", sx: stickyHeadSx },
							{ value: "Status", gridColumn: "auto", sx: stickyHeadSx },
						]}
						gridTemplateColumns="auto auto  1fr auto"
						sx={{ backgroundColor: "#f8fafc" }}
						cellPadding="10px 18px"
						headPadding="10px 16px"
						cellFactory={(row, cell, _index, _key, name) => {
							if (name === "Date") {
								return {
									content: <Typo variant="medium"><DateTimeTooltip value={row.created_at}>{formatDisplayDateTime(row.created_at, { fallback: "" })}</DateTimeTooltip></Typo>,
								};
							}
							if (name === "ID") {
								return { content: <Typo variant="medium">{row.id}</Typo> };
							}
							if (name === "Payment ID") {
								return { content: <Typo variant="medium">{row.decta_payment_id}</Typo> };
							}
							if (name === "Merchant") {
								return { content: <Typo variant="medium">{row.merchant_id}</Typo> };
							}
							if (name === "Fail Reason") {
								return { content: <Typo variant="medium">{row.fail_reason ?? "—"}</Typo> };
							}
							if (name === "Status") {
								const isFailed = row.status === "failed";
								const statusContent = (
									<HBox gap="6px" sx={{ alignItems: "center" }}>
										<StatusBadge value={String(cell ?? "")} />
										{isFailed && row.fail_reason_id != null && <FailReasonHint />}
									</HBox>
								);
								if (isFailed && row.fail_reason) {
									return {
										content: (
											<HoverTooltip
												align="end"
												style={{ cursor: "pointer" }}
												tooltipStyle={{ maxWidth: "500px", width: "max-content" }}
												content={
													<Typo variant="medium" sx={{ color: "#FFFFFF", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
														{row.fail_reason!.includes(",")
															? row.fail_reason!.split(",").map((part, i) => <div key={i}>{part.trim()}</div>)
															: msg("templateFailReason_8784af03", { v0: row.fail_reason })}
													</Typo>
												}>
												{statusContent}
											</HoverTooltip>
										),
									};
								}
								return { content: statusContent };
							}
							return { content: cell != null ? String(cell) : "" };
						}}
					/>
				</div>
			)}

			<Box
				sx={{
					display: "flex",
					gap: "10px",
					alignItems: "center",
					flexWrap: "wrap",
					position: "sticky",
					bottom: 0,
					zIndex: 2,
					backgroundColor: "#f8fafc",
					padding: "10px 0",
				}}>
				<Pagination pagination={paginationData} onPageChange={handlePageChange} />
				<Spacer grow />
				<Button onClick={() => handleRefresh()} title={msg("refresh")} variant="primarySmall" />
			</Box>
			{selectedId !== null && <RecoDetailsPopup key={selectedId} id={selectedId} onClose={() => setSelectedId(null)} />}
		</VBox>
	);
};
